import { useMutation } from '@tanstack/react-query'
import api from '../../config/axios-config'
import { projectMutationKeys, projectQueryKeys } from '../constants'
import { customQueryClient } from '../../config/query-client'
import { getUserId } from '../../authentication/query/use-me-query'

export const useUserLeaveProjectMutation = (options) => {
  const handleMutation = async ({ projectId }) => {
    const userId = getUserId()

    const { data } = await api(`/api/projects/${projectId}/team/${userId}`, {
      method: 'delete'
    })

    return data
  }

  const invalidateRelatedQueries = ({ projectId }) => {
    customQueryClient.invalidateQueries({
      queryKey: [projectQueryKeys.GET_MY_PROJECTS_QUERY],
      refetchType: 'all'
    })
    customQueryClient.invalidateQueries({
      queryKey: [projectQueryKeys.GET_PROJECT_QUERY, projectId]
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    mutationKey: [projectMutationKeys.USER_LEAVE_PROJECT_MUTATION],
    retry: 0,
    ...options,
    onMutate: async ({ projectId }) => {
      // The "my projects" endpoint is built from the user's personalized
      // memberships, and it can lag briefly after leaving a project. We remove
      // the project optimistically, then refetch after a short delay so the API
      // has time to return the updated membership list instead of rehydrating
      // stale data into the cache.

      await customQueryClient.cancelQueries({
        queryKey: [projectQueryKeys.GET_MY_PROJECTS_QUERY]
      })

      const previousQueriesData = customQueryClient.getQueriesData({
        queryKey: [projectQueryKeys.GET_MY_PROJECTS_QUERY]
      })

      customQueryClient.setQueriesData(
        { queryKey: [projectQueryKeys.GET_MY_PROJECTS_QUERY] },
        (oldData) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              content:
                page.content?.filter((project) => project.id !== projectId) ??
                []
            }))
          }
        }
      )

      return { previousQueriesData }
    },
    onSuccess: (data, variables, context) => {
      setTimeout(() => {
        invalidateRelatedQueries(variables)
      }, 5000)
      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      context?.previousQueriesData?.forEach(
        ([previousQueryKey, previousData]) => {
          customQueryClient.setQueryData(previousQueryKey, previousData)
        }
      )

      options?.onError?.(error, variables, context)
    }
  })
}
